import { network } from '@utils'
import { useQuery } from '@tanstack/react-query'
import { canFetchBusinessFeatures, fetchBusiness, type IBusiness } from './businesses'
import { QueryFunction } from './root'

export type IFeatureKey =
  | 'holvi_integration'
  | 'einvoicing'
  | 'invoicing'
  | 'salaries'
  | 'unlimited_users'
  | 'vat_external_reporting'
  | 'payment_initiation'
  | 'financial_statement'
  | 'vat_not_due_release'
  | 'kravia'
  | 'if_insurance'
  | 'holvi_business_accounts'
  | 'ytj'
  | 'noja'
  | 'yle_tax'
  | 'omavero'
  | 'elster'
  | 'handelsregister'

export interface IFeatureV2 {
  key: IFeatureKey
  hidden: boolean
  blocked: boolean
}

export interface IFlagV2 {
  key: string
  enabled: boolean
}

export interface IFeaturesV2Response {
  features: IFeatureV2[]
  flags: IFlagV2[]
}

interface FeatureInstanceProps {
  slug: string
}

export const fetchFeatures: QueryFunction<FeatureInstanceProps, IFeaturesV2Response> = async ({
  slug
}) => {
  const url = `/v2/business/${slug}/features/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const useFeatures = ({ slug }: FeatureInstanceProps) => {
  const {
    data: business,
    isFetching: isBusinessFetching,
    isLoading: isBusinessLoading
  } = useQuery<IBusiness>({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    enabled: Boolean(slug)
  })

  const isBusinessSettled = Boolean(slug) && !isBusinessLoading && !isBusinessFetching

  return useQuery({
    queryKey: [slug, 'features'],
    queryFn: () => fetchFeatures({ slug }),
    enabled: isBusinessSettled && canFetchBusinessFeatures(business)
  })
}
